/**
 * Catálogo de permissões. Cada rota da API exige uma delas; o painel usa as mesmas
 * chaves para esconder ações. `super_admin` tem todas, sempre; `admin`, `operator` e
 * `viewer` começam com o padrão abaixo e podem ser ajustados em Perfis e permissões
 * (o perfil `admin` só pelo Super Administrador).
 */
export const PERMISSIONS = [
    { key: 'dashboard.read', feature: 'Painel', description: 'Ver o painel de indicadores' },
    {
        key: 'reports.read',
        feature: 'Relatórios',
        description: 'Ver o faturamento mensal, diário, por afiliado e por produto',
    },
    { key: 'reports.export', feature: 'Relatórios', description: 'Exportar relatórios para Excel' },
    {
        key: 'affiliates.manage',
        feature: 'Afiliados',
        description: 'Renomear afiliados e unir cadastros duplicados',
    },
    { key: 'imports.read', feature: 'Importações', description: 'Ver as importações e suas linhas' },
    {
        key: 'imports.manage',
        feature: 'Importações',
        description: 'Importar relatórios e substituir períodos já importados',
    },
    { key: 'imports.delete', feature: 'Importações', description: 'Excluir importações' },
    {
        key: 'settings.read',
        feature: 'Configurações',
        description: 'Ver configurações (sem segredos)',
    },
    {
        key: 'settings.manage',
        feature: 'Configurações',
        description: 'Alterar configurações e a integração com a caixa de e-mail',
    },
    { key: 'users.read', feature: 'Usuários', description: 'Ver usuários e sessões' },
    {
        key: 'users.manage',
        feature: 'Usuários',
        description: 'Criar, editar, desativar e redefinir senha',
    },
    {
        key: 'roles.read',
        feature: 'Perfis e permissões',
        description: 'Ver permissões de cada perfil',
    },
    {
        key: 'roles.manage',
        feature: 'Perfis e permissões',
        description: 'Alterar permissões dos perfis',
    },
    { key: 'audit.read', feature: 'Auditoria', description: 'Ver a trilha de auditoria' },
];
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
export const ROLE_LABELS = {
    super_admin: 'Super Administrador',
    admin: 'Administrador',
    operator: 'Operador',
    viewer: 'Visualizador',
};
export const ROLE_DESCRIPTIONS = {
    super_admin: 'Acima de tudo: todas as permissões, sempre. Único que gerencia administradores e o perfil deles.',
    admin: 'Gestão completa dos relatórios, importações e usuários. O Super Administrador pode restringir.',
    operator: 'Opera o dia a dia: importa relatórios, consulta e exporta o faturamento.',
    viewer: 'Somente leitura.',
};
/** Hierarquia: menor = mais poder. */
export const ROLE_RANK = {
    super_admin: 0,
    admin: 1,
    operator: 2,
    viewer: 3,
};
/**
 * Quem pode criar, editar, redefinir a senha ou excluir usuários de um perfil, e atribuir esse
 * perfil. Super Administrador só é tocado por outro Super Administrador; administradores por
 * administradores ou acima; os demais por quem tiver `users.manage`.
 */
export function canManageRole(actor, target) {
    if (target === 'super_admin')
        return actor === 'super_admin';
    if (target === 'admin')
        return actor === 'super_admin' || actor === 'admin';
    return true;
}
/**
 * Quem pode editar as permissões de um perfil (além de ter `roles.manage`).
 * O Super Administrador é fixo; o perfil Administrador só o Super Administrador altera.
 */
export function canEditRolePermissions(actor, target) {
    if (target === 'super_admin')
        return false;
    if (target === 'admin')
        return actor === 'super_admin';
    return true;
}
export const DEFAULT_ROLE_PERMISSIONS = {
    super_admin: [...PERMISSION_KEYS],
    admin: [...PERMISSION_KEYS],
    operator: [
        'dashboard.read',
        'reports.read',
        'reports.export',
        'imports.read',
        'imports.manage',
        'settings.read',
    ],
    viewer: ['dashboard.read', 'reports.read', 'reports.export', 'imports.read'],
};
/** `super_admin` tem tudo, sempre; os demais (inclusive `admin`) dependem da lista configurada. */
export function hasPermission(user, ...keys) {
    if (!user)
        return false;
    if (user.role === 'super_admin')
        return true;
    return keys.every((k) => user.permissions.includes(k));
}
